<script setup lang="ts">
import { ref, computed } from "vue";

export interface TabItem {
  key: string;
  label: string;
  disabled?: boolean;
  badge?: number;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    tabs: TabItem[];
    variant?: "underline" | "pill" | "segmented";
    size?: "sm" | "md";
    panelClass?: string;
  }>(),
  {
    variant: "underline",
    size: "md",
    panelClass: "",
  }
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
}>();

// Unique instance ID for aria-controls / aria-labelledby linkage
const uid = `tabs-${Math.random().toString(36).slice(2, 8)}`;

// Element refs for roving tabindex focus management
const tabRefs = ref<(HTMLButtonElement | null)[]>([]);

const setTabRef = (el: unknown, index: number) => {
  tabRefs.value[index] = el as HTMLButtonElement | null;
};

const selectTab = (key: string) => {
  const tab = props.tabs.find((t) => t.key === key);
  if (tab?.disabled) return;
  emit("update:modelValue", key);
};

const onKeydown = (event: KeyboardEvent, index: number) => {
  const enabledEntries = props.tabs.map((tab, i) => ({ tab, i })).filter(({ tab }) => !tab.disabled);

  const currentPos = enabledEntries.findIndex(({ i }) => i === index);

  let targetIndex: number | null = null;

  switch (event.key) {
    case "ArrowRight":
      event.preventDefault();
      targetIndex = enabledEntries[(currentPos + 1) % enabledEntries.length]?.i ?? null;
      break;
    case "ArrowLeft":
      event.preventDefault();
      targetIndex = enabledEntries[(currentPos - 1 + enabledEntries.length) % enabledEntries.length]?.i ?? null;
      break;
    case "Home":
      event.preventDefault();
      targetIndex = enabledEntries[0]?.i ?? null;
      break;
    case "End":
      event.preventDefault();
      targetIndex = enabledEntries[enabledEntries.length - 1]?.i ?? null;
      break;
  }

  if (targetIndex !== null) {
    selectTab(props.tabs[targetIndex].key);
    tabRefs.value[targetIndex]?.focus();
  }
};

// Tab list container classes
const listClass = computed(() => {
  switch (props.variant) {
    case "segmented":
      return "inline-flex rounded-8 bg-slate-200 p-px-3 gap-px-3";
    case "pill":
      return "flex gap-px-4";
    default:
      return "flex border-b border-slate-200";
  }
});

// Per-tab button classes
const tabClass = (tab: TabItem): string[] => {
  const isActive = tab.key === props.modelValue;
  const isDisabled = !!tab.disabled;

  const sizeClasses = props.size === "sm" ? ["t-small", "px-px-12", "py-px-6"] : ["t-small-bold", "px-px-16", "py-px-8"];

  const base = [
    "inline-flex",
    "items-center",
    "gap-px-6",
    "whitespace-nowrap",
    "transition-colors",
    "duration-fast",
    "ease-standard",
    "focus-ring",
    "outline-none",
    ...sizeClasses,
  ];

  if (isDisabled) {
    base.push("interactive-disabled");
  }

  switch (props.variant) {
    case "underline":
      base.push(
        "-mb-px",
        isActive
          ? "text-app-blue-600 border-b-2 border-app-blue-600"
          : isDisabled
            ? "text-type-muted"
            : "text-type-muted hover:text-type-default"
      );
      break;
    case "pill":
      base.push(
        "rounded-8",
        isActive
          ? "bg-app-blue-600 dark:bg-app-blue-fill text-white"
          : isDisabled
            ? "text-type-muted"
            : "text-type-muted hover:bg-slate-100 hover:text-type-default"
      );
      break;
    case "segmented":
      base.push(
        "flex-1",
        "justify-center",
        "rounded-8",
        isActive
          ? "bg-slate-0 text-type-default shadow-card"
          : isDisabled
            ? "text-type-muted"
            : "text-type-muted hover:text-type-default"
      );
      break;
  }

  return base;
};

// Badge classes — adjust for pill active state so badge stays visible
const badgeClass = (tab: TabItem): string[] => {
  const isActive = tab.key === props.modelValue && props.variant === "pill";
  return [
    "t-xs",
    "inline-flex",
    "min-w-[18px]",
    "items-center",
    "justify-center",
    "rounded-full",
    "px-px-4",
    "leading-tight",
    isActive ? "bg-slate-0 text-app-blue-600" : "bg-app-blue-600 dark:bg-app-blue-fill text-white",
  ];
};
</script>

<template>
  <div>
    <!-- Tablist -->
    <div
      :class="listClass"
      role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :id="`${uid}-tab-${tab.key}`"
        :key="tab.key"
        :ref="(el) => setTabRef(el, index)"
        :aria-controls="`${uid}-panel-${tab.key}`"
        :aria-selected="tab.key === modelValue"
        :aria-disabled="tab.disabled ?? false"
        :tabindex="tab.key === modelValue ? 0 : -1"
        :disabled="tab.disabled"
        role="tab"
        :class="tabClass(tab)"
        @click="selectTab(tab.key)"
        @keydown="onKeydown($event, index)">
        {{ tab.label }}
        <span
          v-if="tab.badge !== undefined && tab.badge > 0"
          :class="badgeClass(tab)">
          {{ tab.badge }}
        </span>
      </button>
    </div>

    <!-- Active tab panel -->
    <div
      :id="`${uid}-panel-${modelValue}`"
      :aria-labelledby="`${uid}-tab-${modelValue}`"
      role="tabpanel"
      tabindex="0"
      :class="panelClass">
      <slot :name="`tab-${modelValue}`" />
    </div>
  </div>
</template>
